import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useTranslation } from 'react-i18next';
import { RequestedAppUpdateInfosManagement } from '../components/RequestedAppUpdateInfosManagement';
import {
  requestedAppUpdateSchema,
  TRequestedAppUpdateSchema,
} from '@/schemas/requested-app-update/requested-app-update.schema';
import {
  useUpsertRequestedAppUpdate,
  useGetFirstRequestedAppUpdate,
} from '@/hooks/requested-app-update.hooks';
import { PhoneIcon } from '@/components/SvgIcons/PhoneIcon';
import Modal from '@/components/Modal';
import { useState } from 'react';
import { showModal } from '@/utils/modal.utils';
import { twMerge } from 'tailwind-merge';

export const RequestAppUpdate = () => {
  const { t } = useTranslation('requestedAppUpdate');
  const [requestFailed, setRequestFailed] = useState(false);
  const { data, isLoading } = useGetFirstRequestedAppUpdate();

  const {
    control,
    register,
    setValue,
    watch,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TRequestedAppUpdateSchema>({
    resolver: zodResolver(requestedAppUpdateSchema),
  });

  const { mutate } = useUpsertRequestedAppUpdate({
    handleSuccess: () => {
      setRequestFailed(false);
      showModal('#app-update-confirmation-modal');
    },
    handleError: () => {
      setRequestFailed(true);
      showModal('#app-update-confirmation-modal');
    },
  });

  const onSubmitRequestedAppUpdate = (
    requestedAppUpdate?: TRequestedAppUpdateSchema
  ) => {
    const requestedAppUpdateWithId = {
      id: data?.data?.id,
      ...requestedAppUpdate,
    };

    mutate(requestedAppUpdateWithId);
  };

  return (
    <>
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <PhoneIcon className="w-[26px] h-[26px] [&>path:first-child]:fill-primary [&>path:nth-child(2)]:fill-success" />
            <div>{t('common:requestedAppUpdate')}</div>
          </div>
        }
      />
      <div className="flex flex-col gap-12">
        <RequestedAppUpdateInfosManagement<TRequestedAppUpdateSchema>
          t={t}
          data={!isLoading && data?.data}
          control={control}
          register={register}
          setValue={setValue}
          watch={watch}
          setError={setError}
          errors={errors}
          submitDisabled={isSubmitting}
          onSubmit={handleSubmit(onSubmitRequestedAppUpdate)}
        />
      </div>
      <Modal
        id="app-update-confirmation-modal"
        title={t('common:confirmation')}
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        centerY
        size="small"
      >
        <div className="flex items-center gap-2">
          <i
            className={twMerge(
              'ki-filled text-2xl',
              requestFailed
                ? 'ki-information-1 text-red-FF2929'
                : 'ki-check-circle text-success'
            )}
          />
          <div>
            {requestFailed
              ? t('common:requestFailed')
              : t('common:requestSuccessful')}
          </div>
        </div>
      </Modal>
    </>
  );
};
